import { useExperiments, useHttpClient, useTranslation } from '@wix/fe-essentials/react';
import { ElementStatus, RuleElement } from '@wix/rules-panel-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { bulkAnnotateImageForContainsText, fetchMediaFile, isImageContainsTextFromBulkAnnotateImagesResponse, isImageContainsTextFromGetFileDescriptorResponse } from '../../../api/fetch-media-file';
import { BiCtaNames } from '../../../bi/types';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useHandleElementClick } from '../../../hooks/use-handle-element-click';
import { isEditor3Host } from '../../../utils/get-editor-host';
import { AltImageRuleContent } from './alt-image-rule-content';
import { AltImageRuleHeader } from './alt-image-rule-header/alt-image-rule-header';
import { useAltTextScreenContext } from './drill-in/alt-text-screen/alt-text-screen-wrapper';
import { useImageSize } from './hooks/image-size/use-image-size';
import { DisabledButtonTooltipTranslationKeySuffix } from './utils/get-disabled-button-tooltip-translation-key-suffix';
export const AltImageRule = _ref => {
  var _altTextDraftDataMap$, _altTextDraftDataMap$2, _altTextDraftDataMap$3, _altTextDraftDataMap$4;
  let {
    pageId,
    url,
    alt: altFromRuleResult,
    status,
    isDecorative: isDecorativeFromRuleResult,
    isOpen,
    toggleElement,
    showLoader,
    elementIndex,
    id,
    previousOpenElementId,
    contentRef,
    headerRef,
    enableDecorativeImages,
    appDefinitionId,
    disabled = false,
    tpaData,
    breakpointId,
    entityId,
    builderLabel
  } = _ref;
  const biLogger = useBiLogger();
  const {
    t
  } = useTranslation();
  const {
    experiments
  } = useExperiments();
  const httpClient = useHttpClient();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const [toolTipMessage, setToolTipMessage] = useState(undefined);
  const [isTextInImage, setIsTextInImage] = useState(false);
  const {
    state: {
      altTextDraftDataMap
    }
  } = useAltTextScreenContext();
  const {
    enlargedImageSize,
    enlargeImage,
    handleImageSize,
    setEnlargeImage
  } = useImageSize(url, isOpen);
  const showTextInImageNotification = experiments.enabled('a11y-wizard.TextInImageNotification');
  const isDecorative = (altTextDraftDataMap == null || (_altTextDraftDataMap$ = altTextDraftDataMap.get(id)) == null ? void 0 : _altTextDraftDataMap$.isDecorative) ?? isDecorativeFromRuleResult;
  const sessionAlt = (altTextDraftDataMap == null || (_altTextDraftDataMap$2 = altTextDraftDataMap.get(id)) == null ? void 0 : _altTextDraftDataMap$2.alt) ?? altFromRuleResult;
  const sessionStatus = (altTextDraftDataMap == null || (_altTextDraftDataMap$3 = altTextDraftDataMap.get(id)) == null ? void 0 : _altTextDraftDataMap$3.status) ?? status;
  const isLoading = (altTextDraftDataMap == null || (_altTextDraftDataMap$4 = altTextDraftDataMap.get(id)) == null ? void 0 : _altTextDraftDataMap$4.status) === ElementStatus.Loading;
  const onStatusChange = useCallback(() => {
    biLogger.elementStatusChange({
      index: elementIndex,
      isAiGenerated: [ElementStatus.Generated, ElementStatus.GeneratedAll].includes(sessionStatus),
      status,
      value: sessionAlt
    });
  }, [biLogger, elementIndex, sessionStatus, status, sessionAlt]);
  const {
    handleClickElement
  } = useHandleElementClick({
    isOpen,
    onOpenElement: () => biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Edit,
      index: elementIndex,
      status
    }),
    toggleElement
  });
  useEffect(() => {
    if (!showTextInImageNotification) {
      return;
    }
    const getIsTextInImage = async () => {
      try {
        const fetchResponse = await fetchMediaFile(httpClient, url);
        if (fetchResponse) {
          let containsText = isImageContainsTextFromGetFileDescriptorResponse(fetchResponse);
          if (containsText === null || containsText === undefined) {
            const annotateResponse = await bulkAnnotateImageForContainsText(httpClient, url);
            containsText = isImageContainsTextFromBulkAnnotateImagesResponse(annotateResponse);
          }
          setIsTextInImage(containsText ?? false);
        }
      } catch (error) {}
    };
    getIsTextInImage();
  }, [httpClient, showTextInImageNotification, url]);
  return /*#__PURE__*/React.createElement(RuleElement, {
    contentComponent: /*#__PURE__*/React.createElement("div", {
      ref: contentRef
    }, /*#__PURE__*/React.createElement(AltImageRuleContent, {
      alt: sessionAlt,
      appDefinitionId: appDefinitionId,
      breakpointId: breakpointId,
      builderLabel: builderLabel,
      enableDecorativeImages: enableDecorativeImages,
      enlargedImageSize: enlargedImageSize,
      enlargeImage: enlargeImage,
      entityId: entityId ?? pageId,
      handleImageSize: handleImageSize,
      id: id,
      index: elementIndex,
      isDecorative: isDecorative,
      isOpen: !disabled && isOpen,
      isTextInImage: isTextInImage,
      setToolTipMessage: setToolTipMessage,
      showLoader: showLoader,
      status: sessionStatus,
      tpaData: tpaData,
      url: url
    })),
    disabled: disabled,
    headerComponent: /*#__PURE__*/React.createElement("div", {
      ref: isOpen ? headerRef : null
    }, /*#__PURE__*/React.createElement(AltImageRuleHeader, {
      alt: sessionAlt,
      disabled: disabled,
      enlargeImage: enlargeImage,
      handleImageSize: handleImageSize,
      id: id,
      index: elementIndex,
      isDecorative: isDecorative,
      isLoading: isLoading,
      isOpen: !disabled && isOpen,
      previousOpenElementId: previousOpenElementId,
      status: sessionStatus
    })),
    iconButtonAriaLabel: t('accessibility-wizard.common.edit.aria-label'),
    iconButtonToolTip: t('accessibility-wizard.common.edit'),
    id: id,
    isLoading: isLoading,
    isOpen: isOpen,
    prefixStatusIconTooltip: sessionStatus === ElementStatus.Warning ? toolTipMessage || t('accessibility-wizard.common.prefix-status-icon.failed-to-generate') : undefined,
    status: sessionStatus,
    width: isEditor3Host(editorHost) ? 'large' : 'normal',
    onClick: () => {
      if (!disabled && !isOpen) {
        setEnlargeImage({
          ...enlargeImage,
          isImageEnlarged: false
        });
      }
      handleClickElement();
    },
    onShowPrefixIconTooltip: () => biLogger.tooltipView({
      tooltipName: DisabledButtonTooltipTranslationKeySuffix.FAIL_TO_AI,
      tooltipOrigin: 'Alt text task'
    }),
    onStatusChange: onStatusChange
  });
};
//# sourceMappingURL=alt-image-rule.js.map